import { BhiDialog } from 'src/UIKit/components/Dialog';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useMediaQuery, useTheme } from '@mui/material';
import React, { CSSProperties, Fragment, useEffect, useRef, useState } from 'react';
import { Accept, useDropzone } from 'react-dropzone';
import { useSelector } from 'react-redux';
import { useAssetsApi } from 'src/modules/assetLibrary/api/AssetsApi';
import { logSafeAxiosError } from 'src/api/axiosClient/errorHandling';
import { FILE_UPLOAD_ERROR_MESSAGE } from 'src/constants/messages';
import { ValidationErrors } from 'src/constants/validations';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import * as Yup from 'yup';
import { FileLinkWithMimeType } from '../../constants/types';
import { acceptTypes, urlValidateSchema } from './constants';
import {
  Cont,
  ContentContainer,
  ErrorMessage,
  FileContainer,
  UrlContainer,
  MobileTabContainer,
} from './styles';
import { useDropzoneStyles } from './useDropzoneStyles';
import { CanvaDesignsPreview } from 'src/modules/canva/components/CanvaDesignsPreview';
import VcfDisplayFromUrl from '../VcfViewer/view';
import { AssetLibrary } from './AssetLibrary';

type AttachFileUrlModalType = {
  show: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  handleFileChange: (link: FileLinkWithMimeType) => void;
  title?: string;
  minHeight?: string | number;
  onlyUpload?: boolean;
  fileAcceptTypes?: Accept;
  allowedFormats?: string;
  link?: string;
};

type TabPanelProps = {
  children?: React.ReactNode;
  name: string;
  value: string;
};

const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, name, ...other } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <div
      role="tabpanel"
      hidden={value !== name}
      id={`simple-tabpanel-${name}`}
      aria-labelledby={`simple-tab-${name}`}
      style={{ width: '100%', maxWidth: '100%' }}
      {...other}
    >
      {value === name && (
        <Box sx={{ 
          p: isMobile ? 1 : 1.5,
          minHeight: isMobile ? 'auto' : 'auto',
          '& > *': {
            width: '100%'
          }
        }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const getProps = (name: string) => {
  return {
    id: `simple-tab-${name}`,
    'aria-controls': `simple-tabpanel-${name}`,
  };
};

export default function AttachFileUrlModal({
  show,
  setShowModal,
  handleFileChange,
  title = 'Attach',
  minHeight,
  onlyUpload,
  fileAcceptTypes,
  allowedFormats,
  link,
}: AttachFileUrlModalType) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState('assetLibrary');
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [url, setUrl] = useState('');
  const { uploadMedia, getAssets } = useAssetsApi();
  const [loading, setLoading] = useState(false);
  const selectedCompanyId = useSelector(getCurrentCompanyId);
  const [hasAssets, setHasAssets] = useState(false);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [isCanvaExporting, setIsCanvaExporting] = useState(false);

  const handleClose = () => {
    setShowModal(false);
  };

  // Read through refs by the upload effect below: both change identity every render, so listing
  // them as dependencies would re-upload the file on every render.
  const handleFileChangeRef = useRef(handleFileChange);
  const handleCloseRef = useRef(handleClose);

  useEffect(() => {
    handleFileChangeRef.current = handleFileChange;
    handleCloseRef.current = handleClose;
  });

  const handleTabChange = (event: React.SyntheticEvent, newTab: string) => {
    setTab(newTab);
  };

  useEffect(() => {
    const fallbackTab = link ? 'preview' : 'upload';

    if (!selectedCompanyId || onlyUpload) {
      setAssetsLoading(false);
      setTab(fallbackTab);
      return;
    }

    // Re-runs on every open/close, so a previous run must not pick the tab.
    let ignore = false;
    setAssetsLoading(true);

    getAssets({ companyId: selectedCompanyId, query: undefined, sort: 'date_created_desc', limit: 1, offset: 0 })
      .then(({ data }) => {
        if (ignore) {
          return;
        }

        setHasAssets(data.count > 0);
        setTab(data.count > 0 ? 'assetLibrary' : fallbackTab);
      })
      .catch(() => {
        if (ignore) {
          return;
        }

        setHasAssets(false);
        setTab(fallbackTab);
      })
      .finally(() => {
        if (!ignore) {
          setAssetsLoading(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [selectedCompanyId, onlyUpload, link, show, getAssets]);

  const validate = async (url: string) => {
    try {
      await urlValidateSchema.validate({ url }, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors = err.inner?.reduce((acc, curr) => {
          if (curr.path) {
            acc[curr.path] = curr?.message;
          }
          return acc;
        }, {} as ValidationErrors);
        setErrors(newErrors);
      }
      return false;
    }
  };

  const validateUrl = async () => {
    const isValid = await validate(url);
    if (isValid) {
      handleFileChange({
        url,
      });
      handleClose();
    }
  };

  const pdfValidator = (file: { type: string; size: number }) => {
    const maxPdfSize = 1024 * 1024;
    const fileSize = 5 * 1024 * 1024;
    if (file.type === 'application/pdf' && file.size > maxPdfSize) {
      return {
        code: 'pdf-size-too-large',
        message: `File upload failed. Only pdf files up to 1MB are allowed. Please check and try again.`,
      };
    }

    if (file.size > fileSize) {
      return {
        code: 'size-too-large',
        message: `File upload failed. Only specific formats up to 5MB are allowed. Please check and try again.`,
      };
    }

    return null;
  };

  const {
    getRootProps,
    getInputProps,
    open,
    acceptedFiles,
    isDragAccept,
    isFocused,
    isDragReject,
    fileRejections,
  } = useDropzone({
    noClick: true,
    accept: fileAcceptTypes ? fileAcceptTypes : acceptTypes,
    noKeyboard: true,
    disabled: loading,
    multiple: false,
    validator: pdfValidator,
  });

  const style: CSSProperties = useDropzoneStyles({
    isFocused,
    isDragAccept,
    isDragReject,
    isMobile,
  });

  useEffect(() => {
    if (fileRejections.length > 0) {
      if (fileRejections[0].errors[0].code) {
        setErrors({
          file: fileRejections[0].errors[0].message,
        });
      }
    } else {
      setErrors({});
    }
  }, [fileRejections]);

  useEffect(() => {
    if (!selectedCompanyId || acceptedFiles.length === 0) {
      return;
    }

    // A second dropped file supersedes the first: the stale upload must not apply its result.
    let ignore = false;
    setLoading(true);

    uploadMedia(acceptedFiles[0], selectedCompanyId, { skipGenericToast: true })
      .then(media => {
        if (ignore) {
          return;
        }

        if (media.status === 'active') {
          handleFileChangeRef.current({
            url: media.url,
            mimeType: media.mimeType,
          });
          handleCloseRef.current();
        } else {
          setErrors({ file: FILE_UPLOAD_ERROR_MESSAGE });
        }
      })
      .catch(e => {
        logSafeAxiosError(e);

        if (!ignore) {
          setErrors({ file: FILE_UPLOAD_ERROR_MESSAGE });
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });

    return () => {
      ignore = true;
      // Cleared here, not in the superseded `finally`: a next run that bails on the guard above
      // would never clear it, and one that doesn't bail sets it straight back.
      setLoading(false);
    };
  }, [acceptedFiles, selectedCompanyId, uploadMedia]);

  const isAssetLibraryOnly = fileAcceptTypes && JSON.stringify(fileAcceptTypes).includes('.vcf')

  const showPreviewTab = !!(onlyUpload && link);
  const showAssetLibraryTab = !onlyUpload;
  const showUploadTab = !isAssetLibraryOnly;
  const showLinkToFileTab = !onlyUpload && !isAssetLibraryOnly;
  const showImportFromCanvaTab = !onlyUpload && !isAssetLibraryOnly;

  const uploadDropzone = (
    <FileContainer>
      <div className="container">
        <Cont {...getRootProps({ style })}>
          <input {...getInputProps()} />
          <p
            style={{
              fontSize: isMobile ? '14px' : '16px',
              margin: isMobile ? '8px 0' : '12px 0',
              textAlign: 'center'
            }}
          >{`Drag and drop file here, or click to select file`}</p>
          <div
            style={{
              textAlign: 'center',
              padding: isMobile ? '0px 16px' : '0px 40px',
              fontSize: isMobile ? '14px' : '16px',
              marginBottom: isMobile ? '8px' : '12px'
            }}
          >
            {`Allowed formats: ${
              allowedFormats
                ? allowedFormats
                : '*.txt, *.vcf, *.ics, *.jpg, *.jpeg, *.png, *.gif, *.3gp, *.mp4, *.pdf, *.m4a'
            }`}
          </div>
          {loading ? (
            <LoadingButton
              loading
              loadingPosition="start"
              startIcon={<SaveIcon />}
              variant="outlined"
              style={{
                margin: isMobile ? '16px 0px 8px 0px' : '25px 0px 10px 0px',
                fontSize: isMobile ? '14px' : '16px',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '200px' : 'none'
              }}
            >
              Loading
            </LoadingButton>
          ) : (
            <Button
              component="label"
              role={undefined}
              variant="contained"
              tabIndex={-1}
              onClick={open}
              disabled={loading}
              startIcon={<CloudUploadIcon />}
              style={{
                margin: isMobile ? '16px 0px 8px 0px' : '25px 0px 10px 0px',
                fontSize: isMobile ? '14px' : '16px',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '200px' : 'none'
              }}
            >
              {link ? 'Replace File' : 'Upload'}
            </Button>
          )}
        </Cont>
      </div>
      <ErrorMessage style={{ color: theme.palette.error.main }}>{errors.file}</ErrorMessage>
    </FileContainer>
  );

  const visibleTabsCount = [
    showPreviewTab,
    showAssetLibraryTab,
    showUploadTab,
    showLinkToFileTab,
    showImportFromCanvaTab,
  ].filter(Boolean).length;

  const showTabs = visibleTabsCount > 1;

  return (
    <Fragment>
      <BhiDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={show}
        fullWidth={isMobile}
        keepMounted={false}
        title={title}
        sx={{
          '& .MuiDialog-root': {
            alignItems: isMobile ? 'center' : 'flex-start',
          },
          '& .MuiDialog-paper': {
            minWidth: isMobile ? 'calc(100% - 32px)' : '600px',
            margin: isMobile ? '16px' : 'auto',
            width: isMobile ? 'calc(100% - 32px)' : '600px',
            maxWidth: isMobile ? 'none' : '600px',
            height: isMobile ? 'auto' : 'unset',
            maxHeight: isMobile ? '80vh' : 'unset',
            minHeight: minHeight || 'unset',
            display: isMobile ? 'flex' : 'block',
            flexDirection: isMobile ? 'column' : 'unset',
            borderRadius: isMobile ? 3 : 'unset',
          }
        }}
      >
        <ContentContainer
          style={{
            padding: isMobile ? '0 16px 16px 16px' : '0 20px 20px 20px',
            maxHeight: isMobile ? '70vh' : 'none',
            overflow: isMobile ? 'auto' : 'visible',
          }}
        >
          {showTabs && <MobileTabContainer>
            <Box sx={{ 
              borderBottom: 1,
              width: '100%',
              borderColor: 'divider',
              marginLeft: isMobile ? '5px' : '0px',
              marginBottom: isMobile ? 1 : 2
            }}>
              <Tabs
                value={tab}
                onChange={handleTabChange}
                aria-label="basic tabs example"
                variant={isMobile ? 'scrollable' : 'standard'}
                scrollButtons={isMobile ? 'auto' : false}
                sx={{

                  '& .MuiTabs-root': {
                    marginLeft: isMobile ? '15px' : '0px',
                    marginRight: isMobile ? '5px' : '0px',
                  },
                  '& .MuiTab-root': {
                    fontSize: isMobile ? '13px' : '16px',
                    minHeight: isMobile ? '44px' : '48px',
                    padding: isMobile ? '8px 12px' : '12px 16px',
                    minWidth: isMobile ? 'auto' : 'auto',
                    textTransform: 'none',
                    marginRight: isMobile ? '15px' : '0px',
                    fontWeight: 'fontWeightMedium',
                    '&.Mui-selected': {
                      color: 'primary.main',
                      marginLeft: isMobile ? '5px' : '0px',
                      marginRight: isMobile ? '15px' : '0px',
                    },
                  },
                  '& .MuiTabs-scrollButtons': {
                    width: isMobile ? '28px' : '40px',
                    height: isMobile ? '44px' : '48px',
                    '&.Mui-disabled': {
                      opacity: 0.3,
                    },
                  },
                  '& .MuiTabs-indicator': {
                    height: isMobile ? '2px' : '3px',
                  },
                  '& .MuiTabs-flexContainer': {
                    gap: isMobile ? '4px' : '8px',
                  }
                }}
              >
              {showPreviewTab && (
                <Tab
                  disabled={isCanvaExporting}
                  label="Preview"
                  value="preview"
                  {...getProps('preview')}
                  sx={{
                    '& .MuiTab-label': {
                      fontSize: isMobile ? '13px' : '16px',
                      lineHeight: isMobile ? '1.2' : '1.5',
                    }
                  }}
                />
              )}
              {showAssetLibraryTab && (
                <Tab
                  label="Asset Library"
                  value="assetLibrary"
                  {...getProps('assetLibrary')}
                  disabled={assetsLoading || !hasAssets || isCanvaExporting}
                  sx={{
                    opacity: assetsLoading || !hasAssets ? 0.5 : 1,
                    '& .MuiTab-label': {
                      fontSize: isMobile ? '13px' : '16px',
                      lineHeight: isMobile ? '1.2' : '1.5',
                    }
                  }}
                />
              )}
              {showUploadTab && <Tab
                label="Upload"
                value="upload"
                {...getProps('upload')}
                disabled={isCanvaExporting}
                sx={{
                  '& .MuiTab-label': {
                    fontSize: isMobile ? '13px' : '16px',
                    lineHeight: isMobile ? '1.2' : '1.5',
                  }
                }}
              />}
              {showLinkToFileTab && (
                <Tab
                  label="Link To File"
                  value="linkToFile"
                  {...getProps('linkToFile')}
                  disabled={isCanvaExporting}
                  sx={{
                    '& .MuiTab-label': {
                      fontSize: isMobile ? '13px' : '16px',
                      lineHeight: isMobile ? '1.2' : '1.5',
                    }
                  }}
                />
              )}
              {showImportFromCanvaTab && (
                <Tab
                  label="Import from Canva"
                  value="importFromCanva"
                  {...getProps('importFromCanva')}
                  disabled={isCanvaExporting}
                  sx={{
                    '& .MuiTab-label': {
                      fontSize: isMobile ? '13px' : '16px',
                      lineHeight: isMobile ? '1.2' : '1.5',
                    }
                  }}
                />
              )}
            </Tabs>
          </Box>
          </MobileTabContainer>}

          <CustomTabPanel value={tab} name="preview">
            {onlyUpload && !!link ? (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'auto',
                  marginTop: '-20px',
                }}
              >
                <VcfDisplayFromUrl  vcfUrl={link} />
                {uploadDropzone}
              </Box>
            ) : (
              <AssetLibrary
                onSelectAsset={handleFileChange}
                onClose={handleClose}
                isMobile={isMobile}
                filterVcfOnly={fileAcceptTypes && JSON.stringify(fileAcceptTypes).includes('.vcf')}
              />
            )}
          </CustomTabPanel>

          <CustomTabPanel value={tab} name="assetLibrary">
            <AssetLibrary
              onSelectAsset={handleFileChange}
              onClose={handleClose}
              isMobile={isMobile}
              filterVcfOnly={fileAcceptTypes && JSON.stringify(fileAcceptTypes).includes('.vcf')}
            />
          </CustomTabPanel>

          <CustomTabPanel value={tab} name="upload">
            {uploadDropzone}
          </CustomTabPanel>
          <CustomTabPanel value={tab} name="linkToFile">
            <UrlContainer>
              <FormControl
                sx={{
                  m: isMobile ? 0.5 : 1,
                  marginBottom: 0,
                }}
                variant="outlined"
                error={!!errors.url}
              >
                <OutlinedInput
                  id="outlined-adornment-weight"
                  endAdornment={<InputAdornment position="end">File URL</InputAdornment>}
                  aria-describedby="outlined-weight-helper-text"
                  inputProps={{
                    'aria-label': 'weight',
                  }}
                  value={url}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const withHttps = (url: string) =>
                      url && !/^(http(s?)):\/\//i.test(url) ? `https://${url}` : url;
                    const withHttpsUrl = withHttps(e.target.value);
                    setUrl(withHttpsUrl);
                  }}
                />
                <FormHelperText id="outlined-weight-helper-text">{errors.url}</FormHelperText>
              </FormControl>
              <FormHelperText
                id="outlined-weight-helper-text"
                sx={{
                  fontSize: isMobile ? '12px' : '14px',
                  marginLeft: isMobile ? '5px' : '0px',
                  marginRight: isMobile ? '5px' : '0px',
                }}
              >
                Free or Public link shorteners are often blocked by carriers due to frequent abuse
                by spammers. Use full URLs or custom link shorteners to ensure deliverability.
              </FormHelperText>
              <Button
                variant="contained"
                style={{
                  alignSelf: isMobile ? 'stretch' : 'end',
                  marginTop: isMobile ? '20px' : '25px',
                  width: isMobile ? '100%' : '130px',
                  fontSize: isMobile ? '14px' : '16px',
                }}
                onClick={validateUrl}
              >
                Attach
              </Button>
            </UrlContainer>
          </CustomTabPanel>
          <CustomTabPanel value={tab} name="importFromCanva">
            <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'auto' }}>
            <CanvaDesignsPreview
                  onExportStart={() => setIsCanvaExporting(true)}
                  onExportEnd={() => setIsCanvaExporting(false)}
                  attachImage={handleFileChange}
                  handleClose={handleClose}
                />
            </Box>
          </CustomTabPanel>
        </ContentContainer>
      </BhiDialog>
    </Fragment>
  );
}
